import type { Bill, BillLogEntry } from '../types'

export function omitBillLogs<T extends { logs?: unknown }>(row: T): Omit<T, 'logs'> {
  const { logs: _logs, ...rest } = row
  return rest
}

export function mergeBillLogAppend(
  logs: BillLogEntry[] | undefined,
  entry: BillLogEntry,
): BillLogEntry[] {
  const list = logs ?? []
  if (list.some((l) => l.id === entry.id)) return list
  return [...list, entry]
}

/** Keep client-appended lines when a shorter snapshot or patch arrives. */
export function mergeBillLogsPreferLonger(
  prev: BillLogEntry[] | undefined,
  incoming: BillLogEntry[] | undefined,
): BillLogEntry[] | undefined {
  if (!prev?.length) return incoming
  if (!incoming?.length) return prev
  const incomingIds = new Set(incoming.map((l) => l.id))
  const extra = prev.filter((l) => !incomingIds.has(l.id))
  if (extra.length === 0) return incoming
  return [...incoming, ...extra]
}

export function preserveBillLogs(prev: Bill | undefined, incoming: Bill): Bill {
  if (!prev) return incoming
  const logs = mergeBillLogsPreferLonger(prev.logs, incoming.logs)
  if (logs === incoming.logs) return incoming
  return { ...incoming, logs }
}

export function isBillAlreadyPaid<T extends { paidAt?: string | null }>(
  bill: T | null | undefined,
): bill is T & { paidAt: string } {
  return bill?.paidAt != null && String(bill.paidAt) !== ''
}

export function readServerPaidAt(data: { paidAt?: unknown } | undefined): string | null {
  const value = data?.paidAt
  if (value == null || value === '') return null
  return String(value)
}

export type BillPayDecision =
  | { action: 'missing' }
  | { action: 'reject'; paidAt: string }
  | { action: 'commit' }

export function decideBillPayWrite(server: {
  exists: boolean
  paidAt?: unknown
}): BillPayDecision {
  if (!server.exists) return { action: 'missing' }
  const paidAt = readServerPaidAt(server)
  if (paidAt) return { action: 'reject', paidAt }
  return { action: 'commit' }
}
